import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import { AppContext } from 'Context';
import { handleDropboxOAuthCallback } from 'Pages/cloudUpload/utils/dropboxOAuthHandler';
import { handleGoogleDriveOAuthCallback } from 'Pages/cloudUpload/utils/googleDriveOAuthHandler';
import { V2SettingsShell } from '../../_shared/V2SettingsShell';

const DropboxIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452l-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6.001 3.822 6-3.822-6-3.822L6 18.371z" />
  </svg>
);

const GoogleDriveIcon = () => (
  <svg width="28" height="28" viewBox="0 0 87.3 78" fill="currentColor">
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" />
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" />
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" />
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" />
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" />
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" />
  </svg>
);

const cloudServices = [
  { id: 'dropbox', name: 'Dropbox', icon: DropboxIcon, color: '#0061FF', enabled: true },
  { id: 'googledrive', name: 'Google Drive', icon: GoogleDriveIcon, color: '#1f7ae0', enabled: true },
];

const ServiceRow = ({ service, connected, onConnect, onDisconnect }) => {
  const Icon = service.icon;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', py: 2.5 }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: 'action.hover',
            color: service.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 600 }}>{service.name}</Typography>
            {connected && (
              <Chip
                size="small"
                icon={<CheckCircleRoundedIcon style={{ fontSize: 14 }} />}
                label="Connected"
                color="success"
                variant="outlined"
                sx={{ height: 22 }}
              />
            )}
            {!service.enabled && (
              <Chip size="small" label="Coming soon" variant="outlined" sx={{ height: 22 }} />
            )}
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {connected?.email || (connected ? 'Connected' : `Import videos from ${service.name}`)}
          </Typography>
        </Box>
      </Stack>
      <Box>
        {service.enabled && !connected && (
          <Button variant="contained" size="small" onClick={() => onConnect(service.id)}>
            Connect
          </Button>
        )}
        {service.enabled && connected && (
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<LinkOffRoundedIcon />}
            onClick={() => onDisconnect(service.id)}
          >
            Disconnect
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export const CloudSettingsV2 = () => {
  const { setAppSnackbar, langText } = useContext(AppContext);
  const navigate = useNavigate();

  const [connectedServices, setConnectedServices] = useState(() => {
    const saved = localStorage.getItem('cloudConnectedServices');
    return saved ? JSON.parse(saved) : { dropbox: null, googledrive: null };
  });

  const handleServiceConnect = (service, connectionData) => {
    const next = { ...connectedServices, [service]: connectionData };
    setConnectedServices(next);
    localStorage.setItem('cloudConnectedServices', JSON.stringify(next));
    setAppSnackbar({
      isVisible: true,
      message: `Successfully connected to ${service === 'googledrive' ? 'Google Drive' : 'Dropbox'}`,
      type: 'success',
    });
  };

  const handleConnect = async (serviceId) => {
    if (serviceId === 'dropbox') {
      const clientId = 'xycymyua8j9yykw';
      const redirectUri = `${window.location.origin}/settings/cloud`;
      const state = Math.random().toString(36).substring(7);
      sessionStorage.setItem('dropboxOAuthState', state);

      const logoutUrl = 'https://www.dropbox.com/logout';
      const authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${clientId}&response_type=token&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&force_reapprove=true&force_reauthentication=true`;

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = logoutUrl;
      document.body.appendChild(iframe);
      setTimeout(() => {
        document.body.removeChild(iframe);
        window.location.href = authUrl;
      }, 1000);
    } else if (serviceId === 'googledrive') {
      const clientId = '526946985013-7ol8pss03r4cd6dqbijso7a4pfrgnhsi.apps.googleusercontent.com';
      const redirectUri = `${window.location.origin}/settings/cloud`;
      const state = Math.random().toString(36).substring(7);
      const scope = 'https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/userinfo.email';
      sessionStorage.setItem('googleDriveOAuthState', state);
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${encodeURIComponent(scope)}&state=${state}&include_granted_scopes=true`;
      window.location.href = authUrl;
    }
  };

  const handleDisconnect = (serviceId) => {
    if (!serviceId) return;
    const next = { ...connectedServices, [serviceId]: null };
    setConnectedServices(next);
    try {
      localStorage.setItem('cloudConnectedServices', JSON.stringify(next));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to save to localStorage:', e);
    }
    const serviceName = serviceId === 'googledrive' ? 'Google Drive' : 'Dropbox';
    setTimeout(() => {
      if (typeof setAppSnackbar === 'function') {
        setAppSnackbar({
          isVisible: true,
          message: `Disconnected from ${serviceName}`,
          type: 'success',
        });
      }
    }, 0);
  };

  useEffect(() => {
    handleDropboxOAuthCallback((authData) => handleServiceConnect('dropbox', authData));
    handleGoogleDriveOAuthCallback((authData) => handleServiceConnect('googledrive', authData));
    // eslint-disable-next-line
  }, []);

  return (
    <V2SettingsShell
      title="Cloud storage"
      description="Connect your cloud storage accounts to import videos."
    >
      <Card sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Box sx={{ px: { xs: 1, md: 1.5 }, pt: 1 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 0.6 }}>
            {langText?.settings?.cloudStorage || 'Connected services'}
          </Typography>
        </Box>
        <Box sx={{ px: { xs: 1, md: 1.5 } }}>
          {cloudServices.map((service, idx) => (
            <React.Fragment key={service.id}>
              <ServiceRow
                service={service}
                connected={connectedServices[service.id]}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
              />
              {idx < cloudServices.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
      </Card>
    </V2SettingsShell>
  );
};
